<template>
  <v-data-table
    class="custom-table"
    :disablePagination="disablePagination"
    :disableSort="!isSortable"
    :footerProps="footerProps"
    :headers="tableHeaders"
    :hideDefaultFooter="hideDefaultFooter"
    :page.sync="pagination.currentPage"
    :itemsPerPage.sync="perpage"
    :items="tableItems"
    :sortBy.sync="sortBy"
    :sortDesc.sync="sortDesc"
    :showExpand="showExpand"
    :singleExpand="showExpand"
    :showSelect="showSelect"
    :search="getSearchedText"
    :serverItemsLength="pagination.totalElementCount"
    :class="[`${isSelectable ? 'selectable' : ''}`, `${customClass ? customClass : ''}`]"
    v-bind="$attrs"
    v-on="$listeners"
    @input="$emit('input', $event)"
    @click:row="onSelect"
    @item-expanded="$emit('onExpansion',$event)"
  >
    <template #top>
      <v-toolbar
        v-if="title"
        flat
      >
        <div class="d-lg-flex align-center w-100">
          <v-toolbar-title
            class="d-flex my-2 my-lg-0 overflow-visible pl-2 justify-space-between titleBar"
            style="width: 100%"
          >
            <span style="font-weight: 700 !important">{{ title }}</span>
            <ActionButtons
              v-if="iconHeader"
              :actionButtons="getActionButtons"
              isInDataTable
              :class="classButton"
            />
          </v-toolbar-title>

          <v-spacer />
          <template v-if="isFilterEnable">
            <v-spacer />
            <v-select
              :items="filterItems"
              class="mt-3"
              chips
              clearable
              :label="$t('forms.labels.filterBy')"
              :multiple="multiple"
              prependIcon="filter_list"
              dense
              itemValue="key"
              itemText="name"
              @input="$emit('filter-type-changed', $event)"
            />
          </template>

          <CsvExport
            v-if="isExportable && tableItems.length"
            :data="tableItems"
            :rowHeaders="exportHeaders"
          />
        </div>
      </v-toolbar>

      <template>
        <slot name="buttonsLineHeader" />
      </template>
      <SearchWithFilters
        v-if="newSearchBar || isSearchable"
        :filters="filters"
        :searchPlaceholder="$t('sites.placeholder.searchBy')"
        :filterName="filterName"
        @onSearch="handleSearch"
      />
      <v-row
        v-if="includeInactiveBtn"
        class="mx-5"
      >
        <v-col
          md="3"
          sm="12"
          class="switch--container"
        >
          <v-switch
            v-model="inactiveStats"
            style="margin:0"
            :label="$t('analyses.types.includeInactive')"
            color="indigo"
            :value="true"
            hideDetails
          />
        </v-col>
      </v-row>
    </template>

    <template
      v-for="(_, slot) of $scopedSlots"
      v-slot:[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>

    <!-- List of actions for each row (mostly used for update/delete the row element) -->
    <template
      v-if="itemActions"
      v-slot:item.action="{ item }"
    >
      <div
        v-if="isCartSection"
        class="d-flex center"
      >
        <VTableAction
          v-if="item.status !== 'Sent'"
          :itemActions="itemActions"
          :verticalActionDots="verticalActionDots"
          :actionWithLabel="actionWithLabel"
          @iconAction="(action)=>$emit('iconAction', item, action)"
        />
      </div>
      <div
        v-else
        :class="['action-buttons', alignActionsHeader === 'end' ? 'action-buttons__end' : '']"
      >
        <VTableAction
          :itemActions="getActionsForItem(item)"
          :item="item"
          :verticalActionDots="verticalActionDots"
          :isCustomIcon="isCustomIconNext"
          :actionWithLabel="actionWithLabel"
          @iconAction="(action)=>$emit('iconAction', item, action)"
        />
      </div>
    </template>
  </v-data-table>
</template>

<script>
import CsvExport from '@/components/shared/widgets/CsvExport';
import VTableAction from '@/components/shared/widgets/VTableAction';
import { debounce } from 'lodash';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import { historyPerpage } from '@/constants/pagination';
import ActionButtons from '../ActionButtons.vue';

export default {
    components: {
        CsvExport,
        VTableAction,
        SearchWithFilters,
        ActionButtons,
    },
    props: {
        disablePagination: {
            type: Boolean,
            default: false,
        },
        elementsPerPage: {
            type: Number,
            default: historyPerpage,
        },
        footerProps: {
            type: Object,
            required: false,
            default: () => ({
                itemsPerPageOptions: [10, 25, 50, 100],
            }),
        },
        headers: {
            type: Array,
            required: true,
        },
        iconHeader: {
            type: String,
            default: null,
        },
        itemActions: {
            type: [Array, Function],
            default: () => [],
        },
        items: {
            type: Array,
            required: true,
        },
        isExportable: {
            type: Boolean,
            default: false,
        },
        isSortable: {
            type: Boolean,
            default: true,
        },
        isSelectable: {
            type: Boolean,
            default: false,
        },
        isSearchable: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        showSelect: {
            type: Boolean,
            default: false,
        },
        showExpand: {
            type: Boolean,
            default: false,
        },
        showRowIcon: {
            type: Boolean,
            default: true,
        },
        verticalActionDots: {
            type: Boolean,
            default: true,
        },
        actionWithLabel: {
            type: Boolean,
            default: false,
        },
        detectSearchInput: {
            type: Boolean,
            default: false,
        },
        includeInactiveStats: {
            type: Boolean,
            default: false,
        },
        includeInactiveBtn: {
            type: Boolean,
            default: false,
        },
        isFilterEnable: {
            type: Boolean,
            default: false,
        },
        filterItems: {
            type: Array,
            default: () => [],
        },
        customClass: {
            type: String,
            default: null,
        },
        isCartSection: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        pagination: {
            type: Object,
            default: () => ({}),
        },
        alignActionsHeader: {
            type: [String],
            default: undefined,
        },
        sortableColumns: {
            type: Array,
            default: () => [],
        },
        newSearchBar: {
            type: Boolean,
            default: false,
        },
        centerColumns: {
            type: Array,
            default: () => ([]),
        },
        notSortableColumns: {
            type: Array,
            default: () => ([]),
        },
        headerActionButtons: {
            type: Array,
            default: () => [],
        },
        isCustomIconNext: {
            type: Boolean,
            default: false,
        },
        sortBy: {
            type: String,
            default: 'name',
        },
        sortDesc: {
            type: Boolean,
            default: false,
        },
        filterName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            search: '',
            dataTableKey: 'data-0', // TODO - change to dynamic Unique Key
            inactiveStats: this.includeInactiveStats,
            debouncedSendSearchInput: debounce(this.sendSearchInput, 500),
            perpage: this.elementsPerPage,
        };
    },
    computed: {
        hideDefaultFooter() {
            return this.disablePagination;
        },
        exportHeaders() {
            return this.headers.map(column => column.value);
        },
        tableHeaders() {
            let headers;
            if (this.isDraggable) {
                headers = [{
                    text: '',
                    value: 'drag_drop',
                    sortable: '',
                }, ...this.headers];
            } else {
                headers = [...this.headers];
            }
            if (this.itemActions.length > 0) {
                headers.push({
                    text: this.$t('controls.actions'),
                    value: 'action',
                    sortable: false,
                    align: this.alignActionsHeader,
                });
            }
            return headers.map(item => ({
                ...item,
                align: this.centerColumns.includes(item.value) ? 'center' : 'start',
                sortable: item.sortable === false ? false : !this.notSortableColumns.includes(item.value),
            }));
        },
        tableItems() {
            return [...this.items];
        },
        getSearchedText() {
            let searchText;
            if (!this.detectSearchInput) searchText = this.search;
            return searchText;
        },
        currentPage() {
            return this.pagination?.currentPage;
        },
        order() {
            const sortLabel = `${this.sortBy}_${((this.sortDesc) ? 'desc' : 'asc')}`;
            return this.$options.filters.lowercase(sortLabel);
        },
        getActionButtons() {
            return this.headerActionButtons?.length ? this.headerActionButtons : [
                {
                    label: this.$t('sitePage.add'),
                    action: () => this.$emit('click-icon-header', this.iconHeader),
                    icon: this.iconHeader,
                    displayIcon: true,
                    color: 'secondary',
                    desktopExtraClass: 'iconAction',
                },
            ];
        },
        classButton() {
            if (window.innerWidth > 600) return 'ml-auto ml-lg-0';
            return '';
        },
    },
    watch: {
        search(val) {
            if (this.detectSearchInput) {
                this.debouncedSendSearchInput(val);
            }
        },
        inactiveStats(val) {
            this.$emit('inactiveStatsChange', val);
        },
        perpage(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.sendQueryParams();
            }
        },
        currentPage(newValue, oldValue) {
            if (oldValue) {
                if (newValue !== oldValue && !this.isInfinitScroll) {
                    this.sendQueryParams();
                }
            }
        },
        order(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.sendQueryParams();
            }
        },
    },
    methods: {
        getActionsForItem(item) {
            if (typeof this.itemActions === 'function') {
                return this.itemActions(item);
            }
            return this.itemActions;
        },
        onSelect(item) {
            if (this.isSelectable) {
                this.$emit('select-row', item);
            }
        },
        sendSearchInput(value) {
            this.$emit('onSearchInput', value);
        },
        buildQueryParams() {
            const queryParams = {
                page: this.pagination?.currentPage,
                perpage: this.perpage,
                order: this.sortableColumns.includes(this.$options.filters.lowercase(this.sortBy)) ? this.order : null,
            };

            if (this.isSearchable) {
                queryParams.search = this.search;
            }
            return queryParams;
        },
        sendQueryParams() {
            this.$emit('sendQueryParams', this.buildQueryParams());
        },
        handleSearch(search) {
            this.search = search;
        },
    },
};
</script>

<style lang='scss' scoped>
    .table-cell-files {
        padding: 0;

        > * {
            line-height: 25px;
        }

        a {
            text-decoration: underline;
        }
    }
    .table-cell-email-invitation {
        > * {
            display: block
        }
    }
    .action-buttons__end {
        justify-content: flex-end;
    }
    ::v-deep table {
        padding: 0px 20px;
    }


</style>
<style lang="scss">
    @media (max-width: 600px) {
        .v-data-footer {
            flex-direction: row;
            justify-content: center;
        }
        .v-data-footer__select {
            width: 100%;
            margin-top: 10px !important;
            margin-bottom: 20px;
            margin-right: 0 !important;
            text-align: center;
            .v-input {
                margin-top: 0px !important;
                margin-left: 15px !important;
                margin-right: 15px !important;
            }
        }
        .v-data-footer__pagination {
            width: 100%;
            margin-top: 20px !important;
        }
    }
    @media screen and (max-width: 600px) {
        .titleBar {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }
</style>
